import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calculator, LineChart, Users, Zap, Star, Activity, ArrowRight, Sparkles } from "lucide-react";
import useSeo from "../../hooks/useSeo";

const TOOLS = [
    {
        to: "/araclar/maliyet-hesaplayici",
        icon: Calculator,
        title: "Maliyet Hesaplayıcı",
        question: "Otel açmak ne kadar tutar?",
        desc: "Yıldız, oda sayısı, lokasyon ve konsepte göre toplam yatırım maliyetini detaylı kategori dağılımıyla görün.",
        accent: "teal",
        active: true,
    },
    {
        to: "/araclar/roi-hesaplayici",
        icon: LineChart,
        title: "ROI Hesaplayıcı",
        question: "Yatırımınız ne zaman döner?",
        desc: "Yıllık gelir, net kâr ve geri dönüş süresini iyimser/normal/kötümser senaryolarla analiz edin.",
        accent: "gold",
        active: true,
    },
    {
        to: "/araclar/personel-planlayici",
        icon: Users,
        title: "Personel Planlayıcı",
        question: "Kaç personel gerekli?",
        desc: "Yıldız standartlarına ve ek alanlara göre departman bazlı personel sayısı ve aylık maliyet.",
        accent: "deep-blue",
        active: false,
    },
    {
        to: "/araclar/enerji-hesaplayici",
        icon: Zap,
        title: "Enerji Hesaplayıcı",
        question: "Aylık enerji faturanız?",
        desc: "Isıtma, soğutma, aydınlatma ve su tüketimini ekipman bazında hesaplayın, tasarruf önerileri alın.",
        accent: "teal",
        active: false,
    },
    {
        to: "/yildiz-standartlari/kontrol",
        icon: Star,
        title: "Yıldız Kontrol",
        question: "Kaç yıldızı hak ediyorsunuz?",
        desc: "Tesisinizin mevcut özelliklerini bakanlık standartlarıyla kıyaslayıp eksikleri tespit edin.",
        accent: "gold",
        active: false,
    },
    {
        to: "/araclar/doluluk-tahmincisi",
        icon: Activity,
        title: "Doluluk Tahmincisi",
        question: "Tahmini doluluk oranınız?",
        desc: "İl, yıldız, açılış tarihi ve sezonsallık bazında 12 aylık doluluk projeksiyonu çıkarın.",
        accent: "deep-blue",
        active: false,
    },
];

export default function Tools() {
    const { t } = useTranslation();
    useSeo({
        title: "Araçlar",
        description: "Otel Pro karar destek araçları: maliyet, ROI, personel, enerji ve doluluk hesaplayıcıları.",
        path: "/araclar",
    });

    return (
        <div className="min-h-[60vh]" data-testid="tools-landing">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20">
                    <span className="text-xs uppercase tracking-[0.25em] text-teal font-semibold inline-flex items-center gap-2">
                        <Sparkles className="w-3.5 h-3.5" /> Karar Destek Araçları
                    </span>
                    <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">Araçlar</h1>
                    <p className="mt-4 text-white/75 max-w-2xl">
                        Yatırımcılar, otel sahipleri ve genel müdürler için profesyonel karar destek araçları. Her hesaplama PDF rapor olarak indirilebilir.
                    </p>
                </div>
            </section>

            <section className="bg-light-bg py-16">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        {TOOLS.map((tool) => (
                            <ToolCard key={tool.to} tool={tool} />
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

function ToolCard({ tool }) {
    const Icon = tool.icon;
    const accentBg = tool.accent === "teal" ? "bg-teal/10 text-teal-700 group-hover:bg-teal group-hover:text-white" : tool.accent === "gold" ? "bg-gold/15 text-gold-700 group-hover:bg-gold group-hover:text-dark-navy" : "bg-deep-blue/10 text-deep-blue group-hover:bg-deep-blue group-hover:text-white";
    return (
        <Link
            to={tool.to}
            data-testid={`tool-card-${tool.title}`}
            className="lift-card group bg-white rounded-lg shadow-md hover:shadow-xl border border-slate-200 hover:border-teal/40 p-7 flex flex-col"
        >
            <div className="flex items-start justify-between">
                <span className={`inline-flex w-14 h-14 rounded-md items-center justify-center transition-colors ${accentBg}`}>
                    <Icon className="w-6 h-6" />
                </span>
                {!tool.active && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-500 font-semibold">
                        Yakında
                    </span>
                )}
            </div>
            <h2 className="mt-5 text-xl font-bold text-dark-navy">{tool.title}</h2>
            <p className="mt-1 text-sm font-semibold text-teal-700">{tool.question}</p>
            <p className="mt-3 text-sm text-deep-blue/80 leading-relaxed flex-1">{tool.desc}</p>
            <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-dark-navy group-hover:text-teal-700">
                Bu Aracı Kullan <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
        </Link>
    );
}
